import { useMemo } from 'react';
import { Filter } from './filter-types';
import { FilterButton } from './FilterButton';
import { useFilterQueryMode } from './useFilterQueryMode';
import { useFilterStateMode } from './useFilterStateMode';
import { FilterElement } from './FilterElement';

export const useFilters = ({
  filters,
  mode = 'query',
}: {
  filters: Filter[];
  mode?: 'query' | 'state';
}) => {
  const useFilterMode =
    mode === 'query' ? useFilterQueryMode : useFilterStateMode;

  const {
    filterValues,
    clearCounts,
    setFilterValue,
    applyFilterWrites,
    visibleFilters,
    setVisibleFilters,
    removeFilter,
    setFilterValues,
  } = useFilterMode({
    filters,
  });

  // Always show permanent filters, including those loaded asynchronously.
  // Derive them instead of syncing state to avoid cascading renders.
  const effectiveVisibleFilters = useMemo(
    () =>
      Array.from(
        new Set([
          ...visibleFilters,
          ...filters.filter((f) => f.permanent).map((f) => f.id),
        ]),
      ),
    [visibleFilters, filters],
  );

  const filtersRender = filters
    .filter((filter) => effectiveVisibleFilters.includes(filter.id))
    .map((filter) => {
      return (
        <FilterElement
          key={filter.id}
          filter={filter}
          filters={filters}
          mode={mode}
          filterValues={filterValues}
          clearCounts={clearCounts}
          setFilterValue={setFilterValue}
          removeFilter={removeFilter}
        />
      );
    });
  return {
    filterButton: (
      <FilterButton
        options={filters.map((filter) => ({
          value: filter.id,
          label: filter.label,
          permanent: filter.permanent,
        }))}
        filters={effectiveVisibleFilters}
        setFilters={setVisibleFilters}
      />
    ),
    filtersRender,
    visibleFilters: effectiveVisibleFilters,
    filterValues,
    setFilterValue,
    applyFilterWrites,
    setVisibleFilters,
    setFilterValues,
  };
};
